import { animalQueries } from '@/queries/animal'
import { orderQueries } from '@/queries/order'
import { subscriptionQueries } from '@/queries/subscription'
import { useQuery } from '@tanstack/vue-query'
import type { DataTag, QueryClient, QueryKey, UndefinedInitialQueryOptions } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { productQueries } from '@/queries/product'

type CriticalQueryFactory<TQueryFnData, TError, TData, TQueryKey extends QueryKey> = () => UndefinedInitialQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey
> & {
  queryKey: DataTag<TQueryKey, TQueryFnData, TError>
}

const query = <TQueryFnData, TError, TData, TQueryKey extends QueryKey>(
  query: CriticalQueryFactory<TQueryFnData, TError, TData, TQueryKey>,
) => ({
  queryKey: query().queryKey,
  ensure: (client: QueryClient) => client.ensureQueryData(query()),
  prefetch: (client: QueryClient) => client.prefetchQuery(query()),
  use: (enabled: Ref<boolean>) => useQuery({ ...query(), enabled }),
})

export const criticalAccountQueries = {
  get blocking() {
    return [
      query(subscriptionQueries.list),
      query(animalQueries.list),
      query(orderQueries.list),
      query(productQueries.list),
    ]
  },
}

/**
 * Loads account state that must be correct before protected pages render.
 * Blocking queries are awaited so inactive subscription/dog state is available
 * immediately;
 */
export const preloadCriticalQueries = async (client: QueryClient): Promise<void> => {
  await Promise.all(criticalAccountQueries.blocking.map((query) => query.ensure(client)))
}
